import type { Track } from '../../types';

export const MUSIC_RESUME_PLAYING_KEY = 'soundpill_music_resume_playing';
export const MUSIC_RESUME_TRACK_KEY = 'soundpill_music_resume_track';
export const MUSIC_RESUME_TIME_KEY = 'soundpill_music_resume_time';

export function readResumePlayingFlag(): boolean {
  return window.sessionStorage.getItem(MUSIC_RESUME_PLAYING_KEY) === '1';
}

export function persistResumePlayingFlag(playing: boolean): void {
  window.sessionStorage.setItem(MUSIC_RESUME_PLAYING_KEY, playing ? '1' : '0');
}

export function clearResumePlayingFlag(): void {
  window.sessionStorage.removeItem(MUSIC_RESUME_PLAYING_KEY);
}

export function readResumePlaybackTime(): number {
  try {
    const raw = window.sessionStorage.getItem(MUSIC_RESUME_TIME_KEY);
    if (!raw) return 0;
    const seconds = Number(raw);
    return Number.isFinite(seconds) && seconds >= 0 ? seconds : 0;
  } catch {
    return 0;
  }
}

export function persistResumePlaybackTime(seconds: number): void {
  if (!Number.isFinite(seconds) || seconds < 0) return;
  window.sessionStorage.setItem(MUSIC_RESUME_TIME_KEY, String(seconds));
}

export function clearResumePlaybackTime(): void {
  window.sessionStorage.removeItem(MUSIC_RESUME_TIME_KEY);
}

export function readResumeTrackFromSession(): Track | null {
  try {
    const raw = window.sessionStorage.getItem(MUSIC_RESUME_TRACK_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Track | null;
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

export function persistResumeTrackToSession(track: Track | null): void {
  if (!track) {
    window.sessionStorage.removeItem(MUSIC_RESUME_TRACK_KEY);
    return;
  }
  window.sessionStorage.setItem(MUSIC_RESUME_TRACK_KEY, JSON.stringify(track));
}

/** 구독 중이거나, 로그인 후 무료 곡(isFree)이면 재생 허용 */
export function canUserPlayTrack(
  track: Track,
  isAuthenticated: boolean,
  hasSubscription: boolean,
): boolean {
  if (!isAuthenticated) return false;
  if (hasSubscription) return true;
  return track.isFree === true;
}

export function redirectToMembershipIfNotSubscribed(
  hasSubscription: boolean,
  track?: Track | null,
): boolean {
  if (hasSubscription) return false;
  if (track?.isFree) return false;
  window.location.href = '/app/membership';
  return true;
}

export function isAudioReadyToPlay(audio: HTMLAudioElement): boolean {
  return audio.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA;
}

/** play() 직후 pause·src 변경으로 나는 AbortError 등 — UI 오류로 올리지 않음 */
export function isBenignPlayInterruption(error: unknown): boolean {
  if (!(error instanceof DOMException)) return false;
  return error.name === 'AbortError' || error.name === 'NotAllowedError';
}

export async function safeAudioPlay(audio: HTMLAudioElement): Promise<boolean> {
  try {
    await audio.play();
    return true;
  } catch (error) {
    if (!isBenignPlayInterruption(error)) {
      console.warn('audio.play() failed', error);
    }
    return false;
  }
}

export function findTrackIndexById(list: Track[], trackId: unknown): number {
  return list.findIndex((track) => String(track.id) === String(trackId));
}

/** 플레이리스트 fetch 후 마지막 재생 곡 복구 */
export function resolveTrackAfterPlaylistFetch(
  fetchedList: Track[],
  resumeTrack: Track | null,
  currentTrack: Track | null,
): { track: Track; index: number } {
  if (fetchedList.length === 0) {
    const fallback = resumeTrack ?? currentTrack;
    if (!fallback) {
      throw new Error('resolveTrackAfterPlaylistFetch: empty playlist and no track to restore');
    }
    return { track: fallback, index: 0 };
  }

  const preferredIds = [resumeTrack?.id, currentTrack?.id].filter((id) => id != null);
  for (const id of preferredIds) {
    const index = findTrackIndexById(fetchedList, id);
    if (index >= 0) {
      return { track: fetchedList[index], index };
    }
  }

  if (resumeTrack) return { track: resumeTrack, index: 0 };
  if (currentTrack) {
    const index = findTrackIndexById(fetchedList, currentTrack.id);
    if (index >= 0) return { track: fetchedList[index], index };
    return { track: currentTrack, index: 0 };
  }

  return { track: fetchedList[0], index: 0 };
}
